import React from "react";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Order } from "../../../../types";

interface RecentOrdersTabProps {
  orders: Order[];
  loading: boolean;
}

const RecentOrdersTab: React.FC<RecentOrdersTabProps> = ({ orders, loading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "#faa805";
      case "confirmed":
        return "#00d4ff";
      case "shipped":
        return "#00d4ff";
      case "delivered":
        return "#0cfc8c";
      case "cancelled":
        return "#ff3366";
      default:
        return "#888888";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-EU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        {t("dashboard.recentOrders")}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Alert severity="info">{t("dashboard.noOrdersYet")}</Alert>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: "transparent",
            borderRadius: "8px",
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          <Table sx={{ minWidth: { xs: 500, sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("dashboard.orderId")}</TableCell>
                <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                  {t("dashboard.date")}
                </TableCell>
                <TableCell>{t("dashboard.customer")}</TableCell>
                <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                  {t("dashboard.items")}
                </TableCell>
                <TableCell>{t("dashboard.total")}</TableCell>
                <TableCell>{t("dashboard.status")}</TableCell>
                <TableCell>{t("dashboard.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    #{order.id.slice(0, 8)}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", sm: "table-cell" },
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {formatDate(order.created_at)}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    {order.shipping_address.full_name}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    {order.items?.length || 0} {t("dashboard.items").toLowerCase()}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    €{order.total_amount.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      size="small"
                      sx={{
                        bgcolor: `${getStatusColor(order.status)}30`,
                        color: getStatusColor(order.status),
                        fontWeight: 600,
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/orders/${order.id}`)}
                      sx={{ color: "#00d4ff" }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RecentOrdersTab;